import { getAllInstancesByName, getAnyInstancesByElement, getSingleInstanceByName, initialiseComponents } from "../../../core";
import Component from "../../component.abstract";
import { notifyFromAlert, notifyUser } from "../../helpers/notifs";
import { NotificationItem } from "../../notification/notification";
import ProfileContainer from "../forms/container/profile-container";
import NavElement from "./nav-element";

const navListMenus = [
    {
      anchor: "recherche",
      title: "Ma recherche",
      paragraph:
        "Je précise mes critères de recherche et je reçois toutes les offres personnalisées",
      svg: "loupe",
      link: "recherche",
      complete: true,
      attribute: "data-profile-search",
      url: "/api/candidatecurrentsearchcomponent"
    },
    {
      anchor:"situation",
      title: "Ma situation actuelle",
      paragraph: "J’augmente ma visibilité dans la base de candidats",
      svg: "compass",
      link: "situation",
      complete: true,
      attribute: "data-profile-situation",
      url: "/api/candidatecurrentsituationcomponent"
    },
    {
      anchor:"cv",
      title: "Mes CV",
      paragraph: "J’organise et je priorise mes CV",
      svg: "temp/cv-menu",
      link: "cv",
      complete: true,
      attribute: "data-profile-cv",
      url: "/api/candidateresumescomponent"
    },
    {
      anchor: "informations",
      title: "Mes informations personnelles",
      paragraph:
        "Je renseigne mon identité et mes paramètres de connexion",
      svg: "userinfo",
      link: "infosperso",
      complete: true,
      attribute: "data-profile-personal",
      url: "/api/candidatepersonalinfoscomponent"
    },
];

export interface Completion {
    "isCompleteCurrentSearch": boolean,
    "isCompleteCurrentSituation": boolean,
    "isCompleteResumes": boolean,
    "isCompletePersonalInfos": boolean
}

export default class NavList extends Component {
    private profileContainer: ProfileContainer;
    private navElements: Array<HTMLElement>;
    public oneClickTips: HTMLElement;

    constructor (
        readonly props: unknown,
        element: HTMLElement
    ) {  
        super(props, element);

        this.navElements = [];
        this.oneClickTips = this.element.querySelector('[data-oneclick-tips]');
    }

    mounted(): void {
        const container: ProfileContainer = getSingleInstanceByName("ProfileContainer");
        if (container) {
            this.profileContainer = container;
        }

        this.displayNav();
    }

    displayNav(): void {    
        const back = this.element.querySelector(".back");
        this.bindBack(back);

        navListMenus.forEach((menu, i) => {
            const nav = document.createElement("div");
            nav.setAttribute(
                "data-profile-nav-element",
                JSON.stringify(menu)
            );
            nav.classList.add("content", "cut", "impact");
    
            if (i === 0) {
                back.after(nav);
            } else {
                const index = i - 1;
                document.querySelectorAll("[data-profile-nav-element]")[index].after(nav);
            }
    
            this.navElements.push(nav);
        });
    
        initialiseComponents();

        //now that all nav elements are initialized we can inform others components.
        PubSub.publish("nav-element-loaded");
    
        // Listen to clicks on navigation elements
        const navElementInstances: Array<NavElement> = getAllInstancesByName("NavElement");
        navElementInstances.forEach(instance => {
            PubSub.subscribe("nav-element.selected", (msg: string, navElement: NavElement) => {
                if (navElement.getElement() === instance.getElement()) {
                    this.hideProfile(true);
                }
            });
        });
    }

    displayError(): void {
        this.displayNotification({
          Icon: 2,
          Message: "Aïe, une erreur est survenue, rechargez la page et réessayez.",
          AnalyticsTrackings: "",
          sticky: true
        });
    }

    displayNotification(notification: NotificationItem): void {
        notifyFromAlert(notification, true);
    }

    bindBack(elt: Element): void {    
        elt.addEventListener("click", event => {
            event.stopImmediatePropagation();

            // TODO: Replace any by form types
            const formInstances = <Array<any>> getAnyInstancesByElement(this.profileContainer.currentForm);
            formInstances.forEach(instance => {
                if (instance.isDirty && instance.isDirty())  {
                    notifyUser({
                        "title": "Avant de quitter la page, pensez à sauvegarder !",
                        "messages": ["Vous avez modifié des informations, cliquez sur « j’enregistre » pour les ajouter à votre compte."],
                        "okButton": "J'enregistre",
                        "okCallback": () => {
                            // Save & stay
                            instance.save();
                        },
                        "cancelButton": "Annuler",
                        "cancelCallback": () => {
                            // Quit without saving
                            this.back();
                        }
                    });
                } else {
                    // Form data wasn't changed
                    this.back();
                }
          });
        });
    }

    back(): void {        
        this.element.querySelector(".navlist").classList.remove("navlist--row");
        this.hideProfile(false);

        this.navElements.forEach(element => {
            element.classList.remove("selected");
        });
    }

    hideProfile(hide: boolean): void {
        const informations = document.querySelector("[data-profile-informations]");
        if (hide && window.innerWidth < 767) {
            informations.classList.add("hide");
        } else {
            informations.classList.remove("hide");
        }

        if (window.innerWidth < 767) {
            setTimeout(() => {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            }, 500);
        }
    }

    setCompletion(completion: Completion): void {
        if (completion.isCompleteCurrentSearch) {
            this.navElements[0].classList.remove("incomplete");
            this.navElements[0].classList.add("complete");
        } else {
            this.navElements[0].classList.remove("complete");
            this.navElements[0].classList.add("incomplete");
        }
    
        if (completion.isCompleteCurrentSituation) {
            this.navElements[1].classList.remove("incomplete");
            this.navElements[1].classList.add("complete");
        } else {
            this.navElements[1].classList.remove("complete");
            this.navElements[1].classList.add("incomplete");
        }
    
        if (completion.isCompleteResumes) {
            this.navElements[2].classList.remove("incomplete");
            this.navElements[2].classList.add("complete");
        } else {
            this.navElements[2].classList.remove("complete");
            this.navElements[2].classList.add("incomplete");
        }
    
        if (completion.isCompletePersonalInfos) {
            this.navElements[3].classList.remove("incomplete");
            this.navElements[3].classList.add("complete");
        } else {
            this.navElements[3].classList.remove("complete");
            this.navElements[3].classList.add("incomplete");
        }
    }

    highlight(activate: boolean, id?: string): void {
        // Remove previous highlights
        this.navElements.forEach(element => {
            element.classList.remove("highlighted");
        });
    
        if (activate) {
            // Get indexes of the element to highlight
            let toHighlight: Array<number>;
            switch(id) {
                case "info1":
                toHighlight = [0];
                break;
                case "info2":
                toHighlight = [1, 2, 3];
                break;
                case "info3":
                toHighlight = [1, 2, 3];
                break;
                case "info4":
                toHighlight = [2, 3];
                break;
            }

            toHighlight.forEach(index => {
                this.navElements[index].classList.add("highlighted");
            });
        }
    }
}